/**
 * Extracts an H2-level table of contents from an article's HTML body and
 * injects matching `id` anchors so the TOC links resolve. Pure string work —
 * safe to run during SSR (no DOM). Existing `id` attributes are preserved.
 *
 * @param {string|null|undefined} html
 * @returns {{ html: string, toc: Array<{ id: string, text: string }> }}
 */
export function extractToc(html) {
    if (!html || typeof html !== 'string') {
        return { html: html ?? '', toc: [] };
    }

    const toc = [];
    const usedIds = new Set();

    const transformed = html.replace(/<h2([^>]*)>([\s\S]*?)<\/h2>/gi, (match, attrs, inner) => {
        const text = stripTags(inner).trim();
        if (!text) return match;

        let id = extractExistingId(attrs) || slugify(text);
        id = ensureUnique(id || 'section', usedIds);
        usedIds.add(id);

        toc.push({ id, text });

        const attrsWithoutId = attrs.replace(/\s+id\s*=\s*("[^"]*"|'[^']*')/i, '');
        return `<h2 id="${id}"${attrsWithoutId}>${inner}</h2>`;
    });

    return { html: transformed, toc };
}

function stripTags(value) {
    return value.replace(/<[^>]*>/g, '');
}

function extractExistingId(attrs) {
    const m = attrs.match(/\sid\s*=\s*"([^"]*)"/i) || attrs.match(/\sid\s*=\s*'([^']*)'/i);
    return m ? m[1] : null;
}

function slugify(text) {
    return text
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80);
}

function ensureUnique(id, usedIds) {
    if (!usedIds.has(id)) return id;
    let i = 2;
    while (usedIds.has(`${id}-${i}`)) i += 1;
    return `${id}-${i}`;
}
